import React, { useState, useMemo, useEffect } from 'react';

import classnames from 'classnames/bind';
import styles from './OrderHistory.module.scss';
import { Button, CancelOrderModal, OrderItem } from '@components';
import { Accordion, AccordionContent, AccordionHeader, AccordionItem, Icon, Typography } from 'gd-design-library';
import { OrderCancellationResponse, OrderExpanded } from '@services/checkout';
import { OrderHistorySkeleton } from './OrderHistory.skeleton';
import { useRouter } from 'next/router';

const CN = 'order-history';
const summaryClassName = `${CN}__summary`;
const detailsClassName = `${CN}__details`;
const cancelButtonClassName = `${CN}__cancel-button`;
const accordionItemClassName = `${CN}__accordion-item`;

const cx = classnames.bind(styles);

interface Props {
  className?: string;
  orders: OrderExpanded[];
  isLoading: boolean;
  onCancelOrder: (orderId: string) => Promise<OrderCancellationResponse>;
}

interface ModalPropsType {
  open: boolean,
  orderId: string,
  message?: string,
  status: 'pending' | 'confirmed' | 'denied'
}

export const OrderHistory: React.FC<Props> = ({
  orders,
  isLoading,
  onCancelOrder
}) => {
  const [ modalProps, setModalProps ] = useState<ModalPropsType>({} as ModalPropsType);
  const [ isCancelLoading, setIsCancelLoading ] = useState(false);
  const [ expandedOrders, setExpandedOrders ] = useState([]);
  const { query } = useRouter();

  const sortedOrders = useMemo(() => orders.slice().sort((a, b) => a.createdAt < b.createdAt ? 1 : -1), [ orders ]);

  const handleCancelOrder = async (orderId: string): Promise<void> => {
    setIsCancelLoading(true);
    const result = await onCancelOrder(orderId);
    setIsCancelLoading(false);
    setModalProps({
      open: true,
      orderId,
      message: result.message,
      status: result.status
    });
  };

  useEffect(() => {
    if (typeof query['order_id'] === 'string') {
      setExpandedOrders([ ...expandedOrders, query['order_id'] ]);
    }
  }, [ query ]);

  return !isLoading ? (
    <>
      <Accordion defaultValue={ [ 'order-history-accordion' ] }>
        <AccordionItem id='order-history-accordion' className={ cx(accordionItemClassName) }>
          <AccordionHeader
            className={ cx(summaryClassName) }
            expandIcon={ <Icon name='arrowDown' width={ 24 } height={ 24 } /> }
          >
            <Typography variant='h4'>Order History</Typography>
          </AccordionHeader>
          <AccordionContent className={ cx(detailsClassName) }>
            <Accordion allowMultipleExpand value={ expandedOrders } onChange={ setExpandedOrders }>
              {sortedOrders.length ?
                sortedOrders.map(order => (
                  <React.Fragment key={ order.id }>
                    <OrderItem order={ order } isExpanded={ expandedOrders.includes(order.id) } />
                    {order.orderState !== 'Cancelled' && order.orderState !== 'Complete' && (
                      <Button
                        iconStart={ <Icon name='cross' size='xs' /> }
                        className={ cx(cancelButtonClassName) }
                        variant='text'
                        onClick={ (): unknown => setModalProps({ open: true, orderId: order.id, status: 'pending' }) }
                      >
                        Cancel Order
                      </Button>
                    )}
                  </React.Fragment>
                )) : <Typography>You have no orders.</Typography>}
            </Accordion>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      {modalProps.open && (
        <CancelOrderModal
          { ...modalProps }
          onCancelOrder={ handleCancelOrder }
          handleClose={ (): void => setModalProps({} as ModalPropsType) }
          isLoading={ isCancelLoading }
        />
      )}
    </>
  ) : <OrderHistorySkeleton />;
};
